// Shared client state, owned here rather than in app.js so that app.js and
// reader.js don't have to import each other — a cycle between two modules that
// both run code at import time is a temporal-dead-zone bug waiting to happen.
import { createApi } from '../../ui/shared/api.ts';
import type { Bookmark, Folder, ReadingStatus, Tag } from '../../ui/shared/types.ts';
import type { DocType } from '../../ui/shared/bookmark-list.ts';

export interface AppState {
  bookmarks: Bookmark[];
  tags: Tag[];
  /** In the user's order. */
  folders: Folder[];
  activeTag: string | null;
  activeStatus: ReadingStatus | '';
  activeType: DocType | '';
  query: string;
  selected: Set<number>;
}

// The app was renamed from Nexus to Altfresh, but these localStorage keys (and
// the other nexus_* ones) keep their names: renaming would log everyone out and
// reset saved reader preferences and reading progress.
export const TOKEN_KEY = 'nexus_token';
export const USER_KEY = 'nexus_user';

export const state: AppState = {
  bookmarks: [],
  tags: [],
  folders: [],
  activeTag: null,
  activeStatus: '',
  activeType: '',
  query: '',
  selected: new Set(),
};

// Set by app.js once its render function exists.
let onBookmarkPatched: (() => void) | null = null;
export function setBookmarkPatchListener(fn: () => void): void {
  onBookmarkPatched = fn;
}

let onUnauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorizedHandler = fn;
}

export const api = createApi({
  baseUrl: window.location.origin,
  getToken: () => localStorage.getItem(TOKEN_KEY),
  onUnauthorized: () => onUnauthorizedHandler?.(),
});

/** Apply a locally-known change without refetching the whole list. */
export function patchBookmarkInList(id: number, changes: Partial<Bookmark>): void {
  const index = state.bookmarks.findIndex((b) => b.id === id);
  if (index === -1) return;
  // Spreading a Partial over a complete value keeps every required field;
  // the index is guarded by the findIndex above.
  state.bookmarks[index] = { ...state.bookmarks[index]!, ...changes } as Bookmark;
  onBookmarkPatched?.();
}
